import { TableFiltersProps } from '@/types/table';
import useFilterFields from '@/utils/hooks/useFilterFields';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import { InstrumentFilters } from '@/XplorPortal/PortalInstruments';
import cn from 'classnames';
import { useMemo } from 'react';
import AppliedFilters from './AppliedFilters';
import FilterSection from './FilterSection';
import GeneralSearchFilter from './GeneralSearchFilter';
import RadioButtonFilter from './RadioButtonFilter';

const deliverableTypes = [
    { label: 'Fx Spot', value: 'Fx Spot' },
    { label: 'Crypto Spot', value: 'Crypto Spot' }
];

const marginTypes = [
    { label: 'Crypto CFD', value: 'Crypto CFD' },
    { label: 'Equity Index CFD', value: 'Equity Index CFD' },
    { label: 'Commodity CFD', value: 'Commodity CFD' },
    { label: 'Futures CFD', value: 'Futures CFD' },
    { label: 'Rolling Fx', value: 'Rolling Fx' }
];

interface InstrumentsTableFiltersProps extends TableFiltersProps {
    filters: InstrumentFilters;
    setFilters: (filters: InstrumentFilters) => void;
    defaultFilters: any;
}

export const InstrumentsTableFilters = (props: InstrumentsTableFiltersProps) => {
    const { opened, filters, setFilters, defaultFilters, globalFilter, setGlobalFilter } = props;

    const lang = useLanguage();
    const { setFilterFields } = useFilterFields(filters, setFilters);

    const { entityType } = useSso();
    const { isMarginClient, isDeliverableClient, isExecutingBroker } = entityType;

    const instrumentTypeFilters: { label: string; value: any }[] = useMemo(() => {
        if (isMarginClient) return marginTypes;
        else if (isDeliverableClient) return deliverableTypes;
        else if (isExecutingBroker) return marginTypes.concat(deliverableTypes);
        else return [];
    }, [entityType]);

    const appliedFilters = useMemo(() => {
        return {
            generalSearch: {
                value: globalFilter,
                label: lang.blotters.generalSearch
            },
            instrumentType: filters.instrumentType
        };
    }, [lang, globalFilter, filters]);

    return (
        <div className="flex flex-col gap-3 text-brand-text">
            {opened && (
                <div className={cn('flex flex-col w-full bg-background-dark border border-neutral-700 rounded-md')}>
                    <FilterSection first>
                        <GeneralSearchFilter
                            globalFilter={globalFilter}
                            setGlobalFilter={setGlobalFilter}
                            onReset={() => {
                                setGlobalFilter('');
                                setFilters(defaultFilters);
                            }}
                        />
                    </FilterSection>
                    {instrumentTypeFilters.length > 0 && (
                        <FilterSection>
                            <RadioButtonFilter
                                name="instrumentType"
                                label={filters['instrumentType'].label}
                                appliedFilters={appliedFilters}
                                radioButtonFilters={instrumentTypeFilters}
                                setFilterFields={setFilterFields}
                            />
                        </FilterSection>
                    )}
                </div>
            )}
            <AppliedFilters appliedFilters={appliedFilters} capitalize={false} />
        </div>
    );
};
